// Import the functions you need from the SDKs you need
import { initializeApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import { 
  getAuth, 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut,
  onAuthStateChanged,
  User
} from "firebase/auth";
import { 
  getFirestore, 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  getDoc,
  query, 
  where,
  Timestamp,
  DocumentData
} from "firebase/firestore";

// Dictionary types
export interface DictionaryEntry {
  phrase: string;
  translation: string;
  definition: string;
  scene_description: string;
}

export interface Dictionary {
  id?: string;
  userId: string;
  title: string;
  year?: string;
  imdbId: string;
  image?: string;
  targetLanguage: string;
  cefrLevel: string;
  speechStyle: string;
  subtitlesLanguage: string;
  entries: DictionaryEntry[];
  createdAt: Timestamp;
}

// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
const firebaseConfig = {
  apiKey: "AIzaSyDV8Jmw-Xd9NzxM8XRAUvls9vK7h9jMaGg",
  authDomain: "linguasub-f9ddf.firebaseapp.com",
  projectId: "linguasub-f9ddf",
  storageBucket: "linguasub-f9ddf.firebasestorage.app",
  messagingSenderId: "625938573765",
  appId: "1:625938573765:web:6d0a5044c5fb826c88c3ac",
  measurementId: "G-XYHNN24TYQ"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const analytics = getAnalytics(app);
const auth = getAuth(app);
const db = getFirestore(app);

// Authentication functions
export const registerUser = async (email: string, password: string) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    return { user: userCredential.user, error: null };
  } catch (error: any) {
    return { user: null, error: error.message };
  }
};

export const loginUser = async (email: string, password: string) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return { user: userCredential.user, error: null };
  } catch (error: any) {
    return { user: null, error: error.message };
  }
};

export const logoutUser = async () => {
  try {
    await signOut(auth);
    return { success: true, error: null };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

// Auth state observer
export const onAuthStateChange = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

// Dictionary functions
export const saveDictionary = async (dictionaryData: Omit<Dictionary, 'id' | 'createdAt'>) => {
  try {
    const dictionaryWithTimestamp = {
      ...dictionaryData,
      createdAt: Timestamp.now()
    };
    
    const docRef = await addDoc(collection(db, 'dictionaries'), dictionaryWithTimestamp);
    return { id: docRef.id, error: null };
  } catch (error: any) {
    console.error("Error saving dictionary:", error);
    return { id: null, error: error.message };
  }
};

export const getUserDictionaries = async (userId: string) => {
  try {
    const q = query(collection(db, 'dictionaries'), where('userId', '==', userId));
    const querySnapshot = await getDocs(q);
    
    const dictionaries: Dictionary[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data() as Omit<Dictionary, 'id'>;
      dictionaries.push({
        ...data,
        id: doc.id
      });
    });
    
    // Sort by createdAt, newest first
    return { dictionaries: dictionaries.sort((a, b) => b.createdAt.seconds - a.createdAt.seconds), error: null };
  } catch (error: any) {
    console.error("Error getting dictionaries:", error);
    return { dictionaries: [], error: error.message };
  }
};

export const getDictionary = async (dictionaryId: string) => {
  try {
    const docRef = doc(db, 'dictionaries', dictionaryId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data() as Omit<Dictionary, 'id'>;
      return { 
        dictionary: {
          ...data,
          id: docSnap.id
        }, 
        error: null 
      };
    } else {
      return { dictionary: null, error: 'Dictionary not found' };
    }
  } catch (error: any) {
    console.error("Error getting dictionary:", error);
    return { dictionary: null, error: error.message };
  }
};

export { app, analytics, auth, db }; 